"use client";

import React, { useMemo } from "react";
import Header from "../global/partials/Header";
import Footer from "../global/partials/Footer";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import HeroSection from "../pageComponents/home/HeroSection";
import HeroQuotesCarousel from "../pageComponents/home/HeroQuotesCarousel";
import TradingCompetitionBanner from "../pageComponents/home/TradingCompetitionBanner";
import IntroSection from "../pageComponents/home/IntroSection";
import TailoredFundingSection from "../pageComponents/home/TailoredFundingSection";
import TestimonialsSection from "../pageComponents/global/sections/TestimonialsSection";
import JourneySectionV2 from "../pageComponents/home/JourneySectionV2";
import HowItWorksSection from "../pageComponents/home/HowItWorksSection";
import WhyIqSection from "../pageComponents/home/WhyIqSection";
import WhyIqSectionV2 from "../pageComponents/home/WhyIqSectionV2";
import TeamSection from "../pageComponents/home/TeamSection";

import HomeStoriesPayoutsSection from "../pageComponents/home/HomeStoriesPayoutsSection";
import KeyStatsSection from "../pageComponents/global/sections/KeyStatsSection";
import ContentCommunitySection from "../pageComponents/home/ContentCommunitySection";
import FeaturedQuoteSection from "../pageComponents/global/sections/FeaturedQuoteSection";
import ContentVideosSection from "../pageComponents/home/ContentVideosSection";
import WhatsappQrSection from "../pageComponents/home/WhatsappQrSection";
import ScrollingBanner from "../pageComponents/global/ScrollingBanner";
import HeaderV2 from "../global/partials/HeaderV2";
import CertificateSectionV2 from "../pageComponents/global/sections/CertificateSectionV2";
import TestimonialsSectionV2 from "../pageComponents/global/sections/TestimonialsSectionV2";
import WhatSetsApartSection from "../pageComponents/global/sections/WhatSetsApartSection";
import StatsBarSection from "../pageComponents/home/StatsBarSection";
import TuvSection from "../pageComponents/home/TuvSection";
import WorldMapSection from "../pageComponents/home/WorldMapSection";
import PayoutToast from "../pageComponents/global/PayoutToast";
import LaunchEventWidget from "../global/LaunchEventWidget";
import TradingCompetitionPopup from "@/components/global/TradingCompetitionPopup";
import { useTradingCompetitionPopupLive } from "@/hooks/useTradingCompetitionPopupLive";
import YoutubeLiveWidget from "../global/YoutubeLiveWidget";
import { useLaunchEventLive } from "@/hooks/useLaunchEventLive";
import { useCampaignLive } from "@/hooks/useCampaignLive";
import type { IScheduledCampaign } from "@/lib/scheduledCampaign";
import { useLivestream } from "@/components/global/livestream/FloatingLivestreamProvider";
import { stegaClean } from "@sanity/client/stega";
import TopBanner from "../global/TopBanner";
import PromoBannerDialog from "../global/PromoBannerDialog";
import BirthdayTopBanner from "../global/BirthdayTopBanner";
import BirthdayPopUp from "../global/BirthdayPopUp";
import SummerSaleTopBanner from "@/components/global/SummerSaleTopBanner";
import SummerSalePopup from "@/components/global/SummerSalePopup";
import { useFlashSale } from "@/components/global/FlashSaleProvider";
import { resolveFlashBanners } from "@/lib/flashSale";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";

export default function PageHomeJSX({ data }: { data: PageHomeSchema }) {
  const { config: flashSale, campaign: flashCampaign } = useFlashSale();
  // The launch widget and the payout toast share the bottom-left corner, so
  // the toast stands down for the duration of the launch campaign.
  const { live: launchEventLive } = useLaunchEventLive(data?.launchEvent, data?.settings?.language);
  const { live: competitionPopupLive } = useTradingCompetitionPopupLive(data?.tradingCompetitionPopup);
  // Bottom-left corner priority: Launch Event > Trading Competition pop-up >
  // YouTube Live > Payout Toast — only one of the four ever renders at a time.
  const youtubeLiveActive = !!stegaClean(data?.youtubeLiveWidget)?.isEnabled;
  // The auto-detected livestream card (FloatingLivestreamProvider) also sits
  // bottom-left: the payout toast pauses while it is on screen and comes back
  // when the channel goes offline or the viewer closes the card.
  const { isOpen: livestreamOpen } = useLivestream();
  // While a flash sale or a seasonal campaign is live its banner pair
  // replaces the page's own top/promo banner settings, and reverts
  // automatically when it ends — the page settings themselves are never
  // touched.
  const banners = resolveFlashBanners(data?.settings, flashSale, flashCampaign);
  // Trading Competition banner: shown on the English and German home pages unless the editor hides
  // it. Without dates the decision depends on data only, so it renders on the server. Optional dates
  // are checked on the client because the page is ISR-cached: the banner then appears after
  // hydration and switches at the boundaries with no publish. Stega is cleaned first, or its
  // invisible characters break the language comparison and Date parsing.
  const tcBanner = useMemo(
    () => stegaClean(data?.tradingCompetitionBanner ?? null),
    [data?.tradingCompetitionBanner],
  );
  const tcBannerLanguage = stegaClean(data?.settings?.language ?? DEFAULT_LANGUAGE);
  const tcBannerAllowed = (tcBannerLanguage === "en" || tcBannerLanguage === "de") && !tcBanner?.hidden;
  const tcBannerScheduled = !!(tcBanner?.startDate || tcBanner?.endDate);
  // The shared window check needs both bounds, so an open side becomes a far-off date.
  const tcBannerWindow = useMemo<IScheduledCampaign | null>(
    () =>
      tcBannerScheduled
        ? {
            isEnabled: true,
            startDate: tcBanner?.startDate || "1970-01-01T00:00:00Z",
            endDate: tcBanner?.endDate || "9999-12-31T00:00:00Z",
          }
        : null,
    [tcBannerScheduled, tcBanner?.startDate, tcBanner?.endDate],
  );
  const tcBannerInWindow = useCampaignLive(tcBannerWindow, "tcBannerPreview", 60_000);
  const showTcBanner = tcBannerAllowed && (!tcBannerScheduled || tcBannerInWindow);

  return (
    <main className="flex flex-col items-center">
      {data?.summerSale?.isActive && !data?.settings?.hideSummerSale && (
        <SummerSaleTopBanner
          data={data.summerSale}
          ctaUrl={data.summerSale?.ctaUrl || "#"}
          language={data.settings?.language}
        />
      )}
      {data?.summerSale?.isActive && !data?.settings?.hideSummerSale && (
        <SummerSalePopup
          data={data.summerSale}
          ctaUrl={data.summerSale?.ctaUrl || "#"}
          language={data.settings?.language}
        />
      )}
      {data?.birthdayBanner?.isActive && !data?.settings?.hideBirthdayBanner && (
        <BirthdayTopBanner
          data={data.birthdayBanner}
          ctaUrl="https://checkout.iqcapital.io/products?coupon=50FUNDING"
          language={data.settings?.language}
        />
      )}
      {data?.birthdayBanner?.isActive && !data?.settings?.hideBirthdayBanner && (
        <BirthdayPopUp
          data={data.birthdayBanner}
          ctaUrl="https://checkout.iqcapital.io/products?coupon=50FUNDING"
          language={data.settings?.language}
        />
      )}
      {banners.showTopBanner && banners.topBanner && (
        <TopBanner
          data={banners.topBanner}
          language={data.settings?.language}
        />
      )}
      {banners.showPromoBanner && banners.promoBanner && (
        <PromoBannerDialog
          data={banners.promoBanner}
          language={data.settings?.language}
        />
      )}
      {data?.settings?.header && (
        <HeaderV2
          data={data.settings.header}
          headerStyle="light"
          headerType={"main"} // showBorder
          language={data.settings?.language}
        />
      )}
      <HeroSection data={data} />
      {showTcBanner && <TradingCompetitionBanner language={data.settings?.language} />}
      <TailoredFundingSection
        data={data}
        afterBanner={showTcBanner}
      />
      <HeroQuotesCarousel data={data} />
      <IntroSection data={data} />
      <StatsBarSection data={data} />
      <TuvSection data={data} />
      <WorldMapSection data={data} />
      <CertificateSectionV2
        compactMobile
        data={{
          chip: data.certificateChip,
          avatarImages: data.certificateAvatarImages,
          title: data.certificateTitle,
          text: data.certificateText,
          highlightedText: data.certificateHighlightedText,
          images: data.certificateImagesRefs?.length
            ? data.certificateImagesRefs
                .map((c) => c.image)
                .filter((img): img is NonNullable<typeof img> => Boolean(img))
            : data.certificateImages,
          ctaUrl: data.certificateCtaUrl,
        }}
        language={data?.settings?.language ?? DEFAULT_LANGUAGE}
      />
      <TestimonialsSectionV2
        compactMobile
        className="lg:pt-[10rem]"
        language={data.settings?.language}
        theme="dark"
        data={{
          ...data,
          testimonialsCards: data.testimonialsCardsRef ?? data.testimonialsCards,
        }}
      />

      <WhatSetsApartSection data={data} />
      <JourneySectionV2 data={data} />
      {/* Hidden by default: the existing document predates the toggle, so an unset value counts as hidden. */}
      {data.hideHowItWorks === false && <HowItWorksSection data={data} />}
      <WhyIqSectionV2 data={data} />
      <TeamSection
        data={data}
        language={data.settings?.language}
      />

      <HomeStoriesPayoutsSection
        data={data}
        language={data.settings?.language}
      />
      {/* <FeaturedQuoteSection data={data} /> */}
      <WhatsappQrSection data={data} />
      <ContentVideosSection data={data} />
      <ContentCommunitySection data={data} />
      <KeyStatsSection data={{ items: data.keyStatsItems }} />
      {/* <TraderValuesSection data={data} /> */}
      <ScrollingBanner
        className="border-y-0 bg-[#f4f5f5]"
        words={data.scrollingWords?.words}
        wordsI18n={data.scrollingWords?.wordsI18n}
      />
      {data?.settings?.footer && (
        <Footer
          data={data.settings?.footer}
          finalCta={data}
          language={data?.settings?.language}
        />
      )}
      {!launchEventLive && !competitionPopupLive && !youtubeLiveActive && !livestreamOpen && <PayoutToast />}
      <LaunchEventWidget
        data={data?.launchEvent}
        language={data.settings?.language}
      />
      {/* Stands down for the live-stream card too, like the payout toast: the
          broadcast is the more urgent of the two, and the pop-up comes back
          once the card is closed. */}
      {!launchEventLive && !livestreamOpen && (
        <TradingCompetitionPopup
          data={data?.tradingCompetitionPopup}
          language={data.settings?.language}
        />
      )}
      {!launchEventLive && !competitionPopupLive && youtubeLiveActive && <YoutubeLiveWidget data={data?.youtubeLiveWidget} />}
    </main>
  );
}
